import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  Image,
  ActivityIndicator,
  TouchableOpacity
} from "react-native";
import Glob from "src/globalConstants";
import Button from "src/components/Button";
import InputBox from "src/components/InputBox";
import PostSkeleton from "src/components/activityFeeds/PostSkeleton";
import Checkbox from "src/components/Checkbox";
import Icon from "src/components/Icon";
import Dropdown from "src/components/Dropdown";
import HelpText from "src/components/HelpText";
import Question from "src/components/Question";
import Rex from "src/globalState";

const SUBSCRIPTION_INTERVALS_DROPDOWN_OPTIONS = [
  {
    text: "day",
    value: "day"
  },
  {
    text: "week",
    value: "week"
  },
  {
    text: "month",
    value: "month"
  },
  {
    text: "year",
    value: "year"
  }
];

const centsToDollarString = (cents) => {
  return `${(cents / 100 || 0).toFixed(2)}`;
};

export default function Product({
  productID,
  name,
  price,
  description,
  shippable,
  adjustableQuantity = false,
  customerSetsPrice = false,
  subscriptionInterval = null,
  customQuestions,
  image,
  isEditing = false,
  isNew = false,
  isEditingAProduct,
  onSave = () => {},
  onArchive = () => {},
  onCancel = () => {},
  onPressPurchase = null,
  isLoadingProduct = false,
  isLoadingPurchaseButton = false,
  shrinkText = false
}) {
  const originalPriceString = centsToDollarString(price);
  const [productName, setProductName] = useState(name);
  const [productDescription, setProductDescription] = useState(description);
  const [productIsShippable, setProductIsShippable] = useState(shippable);
  const [productPrice, setProductPrice] = useState(originalPriceString);
  const [productQuestions, setProductQuestions] = useState(
    customQuestions || []
  );
  const [showCustomQuestions, setShowCustomQuestions] = useState(false);
  const [questionResponses, setQuestionResponses] = useState({});
  const [productAdjustableQuantity, setProductAdjustableQuantity] = useState(
    adjustableQuantity
  );
  const [productCustomerSetsPrice, setProductCustomerSetsPrice] = useState(
    customerSetsPrice
  );
  const [
    productSubscriptionInterval,
    setProductSubscriptionInterval
  ] = useState(subscriptionInterval);
  const [changedProductQuestions, setChangedProductQuestions] = useState(false);

  useEffect(() => {
    setProductPrice(centsToDollarString(price));
  }, [price]);
  useEffect(() => {
    setProductIsShippable(shippable);
  }, [shippable]);
  useEffect(() => {
    setProductAdjustableQuantity(adjustableQuantity);
  }, [adjustableQuantity]);
  useEffect(() => {
    setProductCustomerSetsPrice(customerSetsPrice);
  }, [customerSetsPrice]);
  useEffect(() => {
    setProductSubscriptionInterval(subscriptionInterval);
  }, [subscriptionInterval]);

  const fontSizeMultiplier = shrinkText ? 0.8 : 1;
  const isEditingADifferentProduct = isEditingAProduct && !isEditing;
  const hasBeenEdited =
    name !== productName ||
    description !== productDescription ||
    shippable !== productIsShippable ||
    adjustableQuantity !== productAdjustableQuantity ||
    customerSetsPrice !== productCustomerSetsPrice ||
    originalPriceString !== productPrice ||
    subscriptionInterval !== productSubscriptionInterval ||
    changedProductQuestions;
  const canSave =
    productName && (productPrice || productCustomerSetsPrice) && hasBeenEdited;

  const toggleProductIsSubscription = () => {
    setProductSubscriptionInterval(
      productSubscriptionInterval ? null : "month"
    );
    // Ensure the customer can't set a price
    setProductCustomerSetsPrice(false);
  };

  const toggleCustomerSetsPrice = (newValue) => {
    setProductCustomerSetsPrice(newValue);
    // Ensure this isn't a subscription
    setProductSubscriptionInterval(null);
  };

  if (isLoadingProduct)
    return (
      <View style={styles.productContainer}>
        <PostSkeleton />
      </View>
    );

  const imageSource = image
    ? { uri: image }
    : Glob.get(productIsShippable ? "cardboardBoxClosed" : "shoppingCart");

  const requiredQuestionsAreUnanswered =
    showCustomQuestions &&
    customQuestions.some(
      (question) =>
        question.isRequired &&
        (!questionResponses[question.question] ||
          (typeof questionResponses[question.question] === "string" &&
            questionResponses[question.question].trim() === ""))
    );

  return (
    <View
      style={{
        ...styles.productContainer,
        shadowRadius: isEditing ? 20 : 5,
        opacity: isEditingADifferentProduct ? 0.3 : 1
      }}
    >
      <View
        style={{
          flexDirection: "row"
        }}
      >
        <Image
          source={imageSource}
          style={{
            width: 80,
            height: 80,
            borderRadius: 15,
            resizeMode: "cover"
          }}
        />
        <View style={{ flex: 1, marginLeft: 15 }}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between"
            }}
          >
            <View style={{ flex: 3, marginRight: 2 }}>
              {isEditing ? (
                <InputBox
                  placeholder="Title"
                  autoCapitalize="words"
                  flexibleWidth
                  openStyle
                  value={productName}
                  onChangeText={setProductName}
                  isInvalid={hasBeenEdited && !productName}
                />
              ) : (
                <Text
                  style={{
                    fontSize: 18 * fontSizeMultiplier,
                    fontWeight: "bold"
                  }}
                >
                  {name}
                </Text>
              )}
            </View>
            <View style={{ flex: 2, marginLeft: 2, alignItems: "flex-end" }}>
              {isEditing ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginLeft: 15
                  }}
                >
                  <Text
                    style={{
                      fontSize: 18 * fontSizeMultiplier,
                      fontWeight: "bold",
                      marginTop: 10
                    }}
                  >
                    $
                  </Text>
                  <InputBox
                    placeholder="Price"
                    flexibleWidth
                    keyboardType="numeric"
                    openStyle
                    value={productPrice}
                    onChangeText={setProductPrice}
                    isInvalid={
                      hasBeenEdited &&
                      !productPrice &&
                      !productCustomerSetsPrice
                    }
                    editable={!productCustomerSetsPrice}
                  />
                </View>
              ) : (
                <>
                  {customerSetsPrice ? (
                    <Text
                      style={{
                        fontSize: 12 * fontSizeMultiplier,
                        marginLeft: 15,
                        textAlign: "right",
                        color: "gray"
                      }}
                    >
                      Custom amount
                    </Text>
                  ) : (
                    <Text
                      style={{
                        fontSize: 18 * fontSizeMultiplier,
                        fontWeight: "bold",
                        marginLeft: 15
                      }}
                    >
                      ${(productPrice || "").replace(".00", "")}
                    </Text>
                  )}
                  {productSubscriptionInterval ? (
                    <Text style={{ fontSize: 12 * fontSizeMultiplier }}>
                      per {productSubscriptionInterval}
                    </Text>
                  ) : null}
                </>
              )}
            </View>
          </View>
          {isEditing ? (
            <>
              <InputBox
                placeholder="Description"
                flexibleWidth
                multiline
                openStyle
                value={productDescription}
                onChangeText={setProductDescription}
              />
              <Checkbox
                style={{ marginTop: 15 }}
                checked={productIsShippable}
                text="Require address for shipping"
                onChange={setProductIsShippable}
              />
              <Checkbox
                checked={productAdjustableQuantity}
                text="Let customers specify quantity"
                onChange={setProductAdjustableQuantity}
              />
              <Checkbox
                checked={productCustomerSetsPrice}
                text="Let customers pick their price"
                onChange={toggleCustomerSetsPrice}
              />
              <Checkbox
                style={{ marginBottom: 15 }}
                checked={!!productSubscriptionInterval}
                text="This is a subscription"
                onChange={toggleProductIsSubscription}
              />
            </>
          ) : (
            <Text style={{ fontSize: 16 * fontSizeMultiplier, color: "gray" }}>
              {description}
            </Text>
          )}
        </View>
      </View>
      {isEditing && !!productSubscriptionInterval && (
        <View>
          <Text style={{ fontWeight: "bold", marginTop: 15 }}>
            Subscription Details
          </Text>
          <View style={{ flexDirection: "row" }}>
            <Text
              style={{
                fontSize: 16,
                marginLeft: 15,
                marginRight: 5,
                marginTop: 16
              }}
            >
              Bill the customer every
            </Text>
            <View style={{ maxWidth: 120 }}>
              <Dropdown
                value={productSubscriptionInterval}
                items={SUBSCRIPTION_INTERVALS_DROPDOWN_OPTIONS}
                flexibleWidth
                onSelect={setProductSubscriptionInterval}
              />
            </View>
          </View>
          <HelpText
            text={`If a customer purchases ${
              productName ? `"${productName}"` : "this"
            }, they will be charged${(() => {
              // Note: This isn't actually used because we don't allow custom prices for subscriptions
              if (productCustomerSetsPrice) return " their chosen amount";
              if (productPrice) return ` $${productPrice}`;
              return "";
            })()} once every ${productSubscriptionInterval} until they cancel.`}
            style={{ marginHorizontal: 10, marginBottom: 10 }}
          />
        </View>
      )}
      {isEditing && productCustomerSetsPrice && (
        <HelpText
          text="Customers will be able to enter any amount they want to pay (e.g. for a donation). You can suggest an amount in the product description."
          style={{ marginHorizontal: 10, marginBottom: 10, marginTop: 10 }}
          noteStyle
        />
      )}
      <View>
        {isEditing && (
          <>
            {productQuestions.length > 0 && (
              <Text
                style={{ fontWeight: "bold", marginTop: 15, marginBottom: 10 }}
              >
                Questions to Ask Customer
              </Text>
            )}
            <View style={{ marginTop: 0 }}>
              {productQuestions.map((question, index) => (
                <Question
                  key={index}
                  index={index}
                  question={question}
                  questions={productQuestions}
                  onChangeQuestions={(updatedQuestions) => {
                    setProductQuestions(updatedQuestions);
                    setChangedProductQuestions(true);
                  }}
                  isEditing
                />
              ))}
              <TouchableOpacity
                onPress={() => {
                  setProductQuestions([
                    ...productQuestions,
                    {
                      question: "",
                      inputType: "text",
                      isRequired: true,
                      id: Date.now()
                    }
                  ]);
                }}
                style={{
                  marginTop: 10,
                  backgroundColor: "#f3f3f3",
                  borderRadius: 5,
                  padding: 10,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Icon
                  icon="cde42390-8051-4509-9bfb-462ff507af63"
                  color="gray"
                  size={20}
                />
                <Text
                  style={{
                    color: "gray",
                    marginLeft: 10,
                    fontWeight: "bold"
                  }}
                >
                  {productQuestions.length < 1
                    ? "Ask the Customer Questions"
                    : "Add Question"}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
      {!isEditing && !onPressPurchase && productIsShippable && (
        <Text
          style={{
            fontSize: 12 * fontSizeMultiplier,
            color: "gray",
            marginTop: 10,
            marginBottom: -10,
            alignSelf: "flex-end"
          }}
        >
          Shippable
        </Text>
      )}
      {!isLoadingProduct && !!onPressPurchase && (
        <>
          {!!showCustomQuestions && (
            <View style={{ marginTop: 10 }}>
              {customQuestions &&
                customQuestions
                  .filter((question) => question.question)
                  .map((question, index) => (
                    <Question
                      key={index}
                      question={question}
                      index={index}
                      value={questionResponses[question.question] || ""}
                      onChangeValue={(value) =>
                        setQuestionResponses({
                          ...questionResponses,
                          [question.question]: value
                        })
                      }
                    />
                  ))}
            </View>
          )}
          {isLoadingPurchaseButton ? (
            <ActivityIndicator size="large" style={{ marginTop: 15 }} />
          ) : (
            <TouchableOpacity
              activeOpacity={0.7}
              style={{
                marginTop: 25,
                marginBottom: -10,
                backgroundColor:
                  Rex.getConfig()?.colors?.button || Glob.get("primaryColor"),
                paddingVertical: 12,
                paddingHorizontal: 20,
                borderRadius: 30,
                alignSelf: "flex-end",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                opacity: requiredQuestionsAreUnanswered ? 0.3 : 1
              }}
              disabled={requiredQuestionsAreUnanswered}
              onPress={() => {
                if (
                  customQuestions &&
                  customQuestions.length > 0 &&
                  !showCustomQuestions
                ) {
                  setShowCustomQuestions(true);
                } else {
                  onPressPurchase(questionResponses);
                }
              }}
            >
              <Text
                style={{
                  color: "white",
                  fontSize: 18,
                  fontWeight: "600",
                  marginRight: 10
                }}
              >
                Checkout
              </Text>
              <Icon
                icon="5429cf21-f257-4ada-964f-a34e34e1b541"
                color="white"
                size={20}
              />
            </TouchableOpacity>
          )}
        </>
      )}
      {isEditing && (
        <View
          style={{
            flexDirection: "row",
            width: "100%",
            marginTop: 25,
            marginBottom: -10
          }}
        >
          {!isNew && (
            <Button
              text="Delete"
              textStyle={{ fontWeight: "bold" }}
              flat
              small
              outline
              flexibleWidth
              style={{ flex: 1 }}
              color={Glob.get("dangerRed")}
              onPress={onArchive}
            />
          )}
          <Button
            text="Cancel"
            textStyle={{ fontWeight: "bold" }}
            flat
            small
            outline
            flexibleWidth
            style={{ flex: canSave ? 1 : 2 }}
            color={Glob.get("primaryColor")}
            onPress={onCancel}
          />
          <Button
            text="Save"
            textStyle={{ fontWeight: "bold" }}
            style={{
              flex: canSave ? 2 : 1,
              opacity: canSave ? 1 : 0.3
            }}
            disabled={!canSave}
            flat
            small
            flexibleWidth
            color={Glob.get("primaryColor")}
            onPress={() =>
              onSave({
                name: productName,
                price: productPrice,
                description: productDescription,
                shippable: productIsShippable,
                adjustableQuantity: productAdjustableQuantity,
                customerSetsPrice: productCustomerSetsPrice,
                subscriptionInterval: productSubscriptionInterval,
                customQuestions: productQuestions.map(
                  ({
                    question,
                    inputType,
                    isRequired,
                    dropdownOptions = null
                  }) => ({
                    question,
                    inputType,
                    isRequired,
                    dropdownOptions
                  })
                ),
                isNew,
                productID,
                isEditing
              })
            }
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  productContainer: {
    marginVertical: 15,
    marginHorizontal: 20,
    paddingHorizontal: 15,
    paddingVertical: 25,
    backgroundColor: "white",
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 0 },
    borderRadius: 10,
    shadowRadius: 5,
    elevation: 3,
    opacity: 1
  }
});
